import { useState } from "react";
import { Stethoscope, Eye, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { DoctorReviewDialog } from "./DoctorReviewDialog";
import { doctorsApi } from "@/lib/api";
import type { Doctor } from "@/lib/api/types";
import { fmtCurrency, fmtRelativeTime, initials } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

type Tab = "PENDING" | "VERIFIED";

export default function DoctorsList() {
  const [tab, setTab] = useState<Tab>("PENDING");
  const [detail, setDetail] = useState<Doctor | null>(null);

  const {
    q, setQ, page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Doctor>({
    key: ["doctors", tab],
    fetcher: tab === "PENDING" ? doctorsApi.listPending : doctorsApi.list,
  });

  return (
    <>
      <PageHeader
        title="Doctors"
        subtitle={`${totalItems} ${tab === "PENDING" ? "awaiting verification" : "verified"} doctors. Review credentials, approve or reject, lock accounts, and see earnings.`}
      />

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
            {(["PENDING", "VERIFIED"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`cursor-pointer rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                  tab === t
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "PENDING" ? "Pending review" : "Verified"}
              </button>
            ))}
            <div className="ml-auto w-56">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search doctors…" />
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={Stethoscope}
              title={isFetching ? "Loading…" : tab === "PENDING" ? "No pending doctors" : "No verified doctors"}
              description={tab === "PENDING" ? "Every doctor application has been reviewed." : "No doctors have been verified yet."}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Specialization</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer" onClick={() => setDetail(d)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-9">
                          {d.photoUrl && <AvatarImage src={d.photoUrl} alt={d.name} />}
                          <AvatarFallback>{initials(d.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">{d.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{d.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{d.specialization ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{d.licenseNumber ?? "—"}</TableCell>
                    <TableCell className="text-sm">
                      {d.consultationFee != null ? fmtCurrency(d.consultationFee, "TZS") : "—"}
                    </TableCell>
                    <TableCell>
                      {d.doctorVerified ? (
                        <Badge variant="success">Verified</Badge>
                      ) : (
                        <Badge variant="warning">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {d.createdAt ? fmtRelativeTime(d.createdAt) : "—"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant={d.doctorVerified ? "outline" : "default"} onClick={() => setDetail(d)}>
                        {d.doctorVerified ? <Eye className="size-3.5" /> : <ShieldCheck className="size-3.5" />}
                        {d.doctorVerified ? "View" : "Review"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DoctorReviewDialog doctor={detail} onClose={() => setDetail(null)} />
    </>
  );
}
